import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, Select, Space,
  message, Popconfirm, Tag, Row, Col, Typography, Radio, InputNumber
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, WarningOutlined
} from '@ant-design/icons';
import { useSimpleStore } from '../stores/simpleStore';
import { getApiUrl } from '../config';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface DetourNotice {
  id: number;
  company_id: number;
  route_id: number;
  start_stop_index: number;
  end_stop_index: number;
  start_stop_name: string;
  end_stop_name: string;
  content_type: string;
  text_content: string | null;
  video_filename: string | null;
  media_id: number | null;
  display_duration: number;
  company_name: string;
  route_number: string;
  route_name: string;
  media_title: string | null;
  media_filename: string | null;
  created_at: string;
}

interface CompanyRoute {
  id: number;
  route_number: string;
  route_name: string;
  company_id: number;
}

interface RouteStop {
  id: number;
  stop_name: string;
  stop_id_external: string;
  stop_order: number;
}

interface MediaFile {
  id: number;
  title: string;
  filename: string;
  original_name: string;
  duration: number;
  mime_type: string;
}

const DetourNoticeManagement: React.FC = () => {
  const { user, selectedCompanyId } = useSimpleStore();
  const [loading, setLoading] = useState(false);
  const [notices, setNotices] = useState<DetourNotice[]>([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [modalVisible, setModalVisible] = useState(false);
  const [editingNotice, setEditingNotice] = useState<DetourNotice | null>(null);
  const [form] = Form.useForm();

  const [routes, setRoutes] = useState<CompanyRoute[]>([]);
  const [stops, setStops] = useState<RouteStop[]>([]);
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [loadingStops, setLoadingStops] = useState(false);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [contentType, setContentType] = useState<string>('text');
  const [selectedStartIndex, setSelectedStartIndex] = useState<number | undefined>();

  const token = localStorage.getItem('adminToken');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // 우회안내 목록 로드
  const loadNotices = async (page = 1, pageSize = 10) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: page.toString(), limit: pageSize.toString() });
      if (selectedCompanyId) params.append('company_id', selectedCompanyId.toString());

      const response = await fetch(getApiUrl(`/api/detour-notices?${params}`), { headers });
      const data = await response.json();

      if (data.success) {
        setNotices(data.data.detour_notices);
        setPagination({
          current: data.data.pagination.page,
          pageSize: data.data.pagination.limit,
          total: data.data.pagination.total
        });
      }
    } catch (error) {
      console.error('우회안내 로드 에러:', error);
      message.error('우회안내 목록을 불러오는데 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  // 노선 목록 로드
  const loadRoutes = async (companyId: number) => {
    try {
      setLoadingRoutes(true);
      const params = new URLSearchParams();
      if (user?.role !== 'admin') params.append('company_id', companyId.toString());

      const response = await fetch(getApiUrl(`/api/company-routes?${params}`), { headers });
      const data = await response.json();

      if (data.success) {
        const filtered = user?.role === 'admin'
          ? data.data.filter((r: CompanyRoute) => r.company_id === companyId)
          : data.data;
        setRoutes(filtered);
      }
    } catch (error) {
      console.error('노선 로드 에러:', error);
    } finally {
      setLoadingRoutes(false);
    }
  };

  // 정류장 목록 로드
  const loadStops = async (routeId: number) => {
    try {
      setLoadingStops(true);
      const response = await fetch(getApiUrl(`/api/targeted-ads/routes/${routeId}/stops`), { headers });
      const data = await response.json();
      if (data.success) {
        setStops(data.data.stops);
      }
    } catch (error) {
      console.error('정류장 로드 에러:', error);
    } finally {
      setLoadingStops(false);
    }
  };

  // 미디어 파일 목록 로드
  const loadMediaFiles = async (companyId: number) => {
    try {
      setLoadingMedia(true);
      const params = new URLSearchParams({ company_id: companyId.toString(), include_system: 'true', limit: '1000' });
      const response = await fetch(getApiUrl(`/api/media/list?${params}`), { headers });
      const data = await response.json();
      if (data.success) {
        setMediaFiles(data.data.filter((m: MediaFile) =>
          m.mime_type && (m.mime_type.startsWith('video/') || m.mime_type === 'application/mp4')
        ));
      }
    } catch (error) {
      console.error('미디어 로드 에러:', error);
    } finally {
      setLoadingMedia(false);
    }
  };

  useEffect(() => {
    if (selectedCompanyId) {
      loadNotices();
      loadRoutes(selectedCompanyId);
      loadMediaFiles(selectedCompanyId);
    }
  }, [selectedCompanyId]);

  useEffect(() => {
    loadNotices(pagination.current, pagination.pageSize);
  }, [user]);

  // 노선 변경
  const handleRouteChange = (routeId: number) => {
    setStops([]);
    setSelectedStartIndex(undefined);
    form.setFieldsValue({ start_stop_index: undefined, end_stop_index: undefined, start_stop_name: undefined, end_stop_name: undefined });
    if (routeId) loadStops(routeId);
  };

  // 시작 정류장 변경
  const handleStartStopChange = (index: number) => {
    setSelectedStartIndex(index);
    const stop = stops.find(s => s.stop_order === index);
    form.setFieldsValue({ start_stop_name: stop?.stop_name, end_stop_index: undefined, end_stop_name: undefined });
  };

  // 끝 정류장 변경
  const handleEndStopChange = (index: number) => {
    const stop = stops.find(s => s.stop_order === index);
    form.setFieldsValue({ end_stop_name: stop?.stop_name });
  };

  // 생성/수정
  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      const requestData = {
        route_id: values.route_id,
        start_stop_index: values.start_stop_index,
        end_stop_index: values.end_stop_index,
        start_stop_name: values.start_stop_name,
        end_stop_name: values.end_stop_name,
        content_type: values.content_type,
        text_content: values.content_type === 'text' ? values.text_content : null,
        media_id: values.content_type === 'video' ? values.media_id : null,
        display_duration: values.display_duration || 30
      };

      const url = editingNotice
        ? getApiUrl(`/api/detour-notices/${editingNotice.id}`)
        : getApiUrl('/api/detour-notices');
      const method = editingNotice ? 'PUT' : 'POST';

      const response = await fetch(url, { method, headers, body: JSON.stringify(requestData) });
      const data = await response.json();

      if (data.success) {
        message.success(editingNotice ? '우회안내가 수정되었습니다' : '우회안내가 생성되었습니다');
        setModalVisible(false);
        form.resetFields();
        loadNotices(pagination.current, pagination.pageSize);
      } else {
        message.error(data.error || '저장 실패');
      }
    } catch (error) {
      console.error('우회안내 저장 에러:', error);
      message.error('우회안내 저장에 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  // 삭제
  const handleDelete = async (id: number) => {
    try {
      const response = await fetch(getApiUrl(`/api/detour-notices/${id}`), { method: 'DELETE', headers });
      const data = await response.json();
      if (data.success) {
        message.success('우회안내가 삭제되었습니다');
        loadNotices(pagination.current, pagination.pageSize);
      } else {
        message.error(data.error || '삭제 실패');
      }
    } catch (error) {
      message.error('삭제에 실패했습니다');
    }
  };

  // 추가 버튼
  const handleAdd = () => {
    setEditingNotice(null);
    setContentType('text');
    setSelectedStartIndex(undefined);
    form.resetFields();
    form.setFieldsValue({ content_type: 'text', display_duration: 30 });
    setModalVisible(true);
  };

  // 수정 버튼
  const handleEdit = (record: DetourNotice) => {
    setEditingNotice(record);
    setContentType(record.content_type);
    setSelectedStartIndex(record.start_stop_index);
    if (record.route_id) loadStops(record.route_id);
    form.setFieldsValue({
      route_id: record.route_id,
      start_stop_index: record.start_stop_index,
      end_stop_index: record.end_stop_index,
      start_stop_name: record.start_stop_name,
      end_stop_name: record.end_stop_name,
      content_type: record.content_type,
      text_content: record.text_content,
      media_id: record.media_id,
      display_duration: record.display_duration
    });
    setModalVisible(true);
  };

  // 테이블 컬럼
  const columns = [
    {
      title: '운수사',
      dataIndex: 'company_name',
      key: 'company_name',
      width: 100
    },
    {
      title: '노선',
      key: 'route',
      width: 100,
      render: (_: any, record: DetourNotice) => (
        <Tag color="blue">{record.route_number}</Tag>
      )
    },
    {
      title: '우회 구간',
      key: 'section',
      width: 200,
      render: (_: any, record: DetourNotice) => (
        <Text>{record.start_stop_name} → {record.end_stop_name}</Text>
      )
    },
    {
      title: '유형',
      dataIndex: 'content_type',
      key: 'content_type',
      width: 80,
      render: (type: string) => (
        <Tag color={type === 'text' ? 'green' : 'purple'}>
          {type === 'text' ? '텍스트' : '영상'}
        </Tag>
      )
    },
    {
      title: '내용',
      key: 'content',
      width: 200,
      ellipsis: true,
      render: (_: any, record: DetourNotice) => (
        record.content_type === 'text'
          ? <Text ellipsis>{record.text_content}</Text>
          : <Text type="secondary">{record.media_title || record.video_filename}</Text>
      )
    },
    {
      title: '표출시간',
      dataIndex: 'display_duration',
      key: 'display_duration',
      width: 80,
      render: (v: number) => `${v}초`
    },
    {
      title: '작업',
      key: 'actions',
      width: 120,
      render: (_: any, record: DetourNotice) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm title="삭제하시겠습니까?" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={4} style={{ margin: 0 }}>
              <WarningOutlined style={{ marginRight: 8 }} />
              우회안내 관리
            </Title>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              disabled={!selectedCompanyId}
            >
              우회안내 추가
            </Button>
          </Col>
        </Row>

        {!selectedCompanyId && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
            좌측 메뉴에서 운수사를 선택해주세요
          </div>
        )}

        {selectedCompanyId && (
          <Table
            columns={columns}
            dataSource={notices}
            rowKey="id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showTotal: (total) => `총 ${total}개`
            }}
            onChange={(p) => loadNotices(p.current, p.pageSize)}
            size="middle"
          />
        )}
      </Card>

      {/* 추가/수정 모달 */}
      <Modal
        title={editingNotice ? '우회안내 수정' : '우회안내 추가'}
        open={modalVisible}
        onCancel={() => { setModalVisible(false); form.resetFields(); }}
        footer={null}
        width={700}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="route_id" label="노선 선택" rules={[{ required: true, message: '노선을 선택해주세요' }]}>
            <Select
              placeholder="노선을 선택하세요"
              loading={loadingRoutes}
              onChange={handleRouteChange}
              showSearch
              optionFilterProp="children"
            >
              {routes.map(route => (
                <Option key={route.id} value={route.id}>
                  {route.route_number} - {route.route_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="start_stop_index" label="우회 시작 정류장" rules={[{ required: true, message: '시작 정류장을 선택해주세요' }]}>
                <Select
                  placeholder="시작 정류장"
                  loading={loadingStops}
                  onChange={handleStartStopChange}
                  showSearch
                  optionFilterProp="children"
                >
                  {stops.map(stop => (
                    <Option key={stop.stop_order} value={stop.stop_order}>
                      [{stop.stop_order}] {stop.stop_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="start_stop_name" hidden><Input /></Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="end_stop_index" label="우회 끝 정류장" rules={[{ required: true, message: '끝 정류장을 선택해주세요' }]}>
                <Select
                  placeholder="끝 정류장"
                  loading={loadingStops}
                  onChange={handleEndStopChange}
                  showSearch
                  optionFilterProp="children"
                >
                  {stops.filter(s => selectedStartIndex !== undefined ? s.stop_order > selectedStartIndex : true).map(stop => (
                    <Option key={stop.stop_order} value={stop.stop_order}>
                      [{stop.stop_order}] {stop.stop_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="end_stop_name" hidden><Input /></Form.Item>
            </Col>
          </Row>

          <Form.Item name="content_type" label="콘텐츠 유형" rules={[{ required: true }]}>
            <Radio.Group onChange={(e) => setContentType(e.target.value)}>
              <Radio.Button value="text">텍스트</Radio.Button>
              <Radio.Button value="video">영상</Radio.Button>
            </Radio.Group>
          </Form.Item>

          {contentType === 'text' && (
            <Form.Item name="text_content" label="안내 텍스트" rules={[{ required: true, message: '안내 텍스트를 입력해주세요' }]}>
              <TextArea rows={4} placeholder="예: 도로 공사로 인해 우회합니다" />
            </Form.Item>
          )}

          {contentType === 'video' && (
            <Form.Item name="media_id" label="영상 선택" rules={[{ required: true, message: '영상을 선택해주세요' }]}>
              <Select
                placeholder="영상을 선택하세요"
                loading={loadingMedia}
                showSearch
                optionFilterProp="children"
              >
                {mediaFiles.map(media => (
                  <Option key={media.id} value={media.id}>
                    {media.title || media.original_name} ({media.duration ? `${Math.round(media.duration)}초` : '알 수 없음'})
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item name="display_duration" label="표출 시간 (초)" initialValue={30}>
            <InputNumber min={5} max={120} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => { setModalVisible(false); form.resetFields(); }}>취소</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingNotice ? '수정' : '생성'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DetourNoticeManagement;
